import { concat, Observable, of, share, tap } from 'rxjs';

import { HttpCacheService } from './http-cache.service';

interface HasCacheService {
  cache: HttpCacheService;
}

const generateCacheKey = (
  args: Array<[{ [key: string]: number | string } | string | boolean | number]>,
) => {
  return args.reduce((serializedArgs: string, arg: unknown) => {
    const prefix = serializedArgs ? `${serializedArgs}_` : '';
    if ('object' === typeof arg) {
      return `${prefix}${Object.keys(arg)
        .sort()
        .map((key) => `${key}_${arg[key as keyof typeof arg]}`)
        .join('_')}`;
    }
    if (['string', 'boolean', 'number'].includes(typeof arg)) {
      return `${prefix}${arg.toString()}`;
    }
    return prefix;
  }, '');
};

export function HttpRequestCache() {
  return function <T>(
    target: HasCacheService,
    methodName: string,
    descriptor: TypedPropertyDescriptor<(...args: any) => Observable<T>>,
  ) {
    const originalMethod = descriptor.value;
    descriptor.value = function (this: HasCacheService, ...args: any[]) {
      const serializedArgs = generateCacheKey(args);
      const cacheKey = `${target.constructor.name}_${methodName}_${serializedArgs}`;
      if (!this.cache) {
        throw Error('Please implement HttpCache interface');
      }
      const cacheService = this.cache;
      const cachedValue = cacheService.getCache<T>(cacheKey);

      if (cachedValue && 'inflight' === cachedValue.status) {
        return cachedValue.observable;
      }

      const originalMethod$ = (
        originalMethod.apply(this, args) as Observable<T>
      ).pipe(
        tap((response: T) => cacheService.updateItem(cacheKey, response)),
        share(),
      );

      if (!cachedValue) {
        cacheService.startCache(cacheKey, originalMethod$);
        return originalMethod$;
      }

      return concat(of(cachedValue.item), originalMethod$);
    };
    return descriptor;
  };
}
